'use client';

import { ServerTimeContext } from '@contexts/ServerTimeContext';
import { isWithinInterval } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { getAdjustedClientTime } from 'utils';
import { Sale } from './types';
import { useFlashDeal } from './useFlashDeal';

/**
 * Returns true if there is a currently active sale of any type.
 * Sets timeouts so that if the sale start or end date is hit, the UI updates accordingly with the new sale status.
 */
export const useIsActiveSale = (sale?: Sale) => {
  const { serverTimeOffsetInMs } = useContext(ServerTimeContext);

  const [isSaleActive, setIsSaleActive] = useState<boolean>(
    sale?.dates
      ? isWithinInterval(
          getAdjustedClientTime(new Date(), serverTimeOffsetInMs),
          {
            start: sale.dates.starts,
            end: sale.dates.ends,
          }
        )
      : false
  );

  useEffect(() => {
    if (!sale) {
      setIsSaleActive(false);
      return;
    }

    let timeout: ReturnType<typeof setTimeout> | undefined;
    const MAX_TIMER = 172800000; // 2 days

    // Self-arming boundary chain: each pass re-syncs the state from the
    // offset-adjusted clock and schedules the next wake-up — the next sale
    // boundary when it's within MAX_TIMER, or a MAX_TIMER re-check otherwise,
    // so a tab left open across a far-off boundary still flips on time. The
    // timers drive the chain themselves rather than relying on effect
    // re-runs: a re-check that lands on an unchanged state wouldn't
    // re-render, which would strand the chain.
    const arm = () => {
      const adjustedNow = getAdjustedClientTime(
        new Date(),
        serverTimeOffsetInMs
      );
      const isSaleActiveNow = isWithinInterval(adjustedNow, {
        start: sale.dates.starts,
        end: sale.dates.ends,
      });
      // Functional update: `arm` also runs from timer callbacks, where a
      // closure read of the state would be stale. Also re-syncs the
      // offset-0 initial state once the corrected offset arrives.
      setIsSaleActive((previous) =>
        previous === isSaleActiveNow ? previous : isSaleActiveNow
      );

      const isSaleUpcoming = adjustedNow < sale.dates.starts;
      const boundary = isSaleUpcoming
        ? sale.dates.starts
        : isSaleActiveNow
          ? sale.dates.ends
          : undefined;
      if (!boundary) {
        // Sale over — nothing left to schedule.
        return;
      }
      // The 1ms floor lands the next pass strictly past the boundary:
      // isWithinInterval is end-inclusive, so a pass at exactly `ends` still
      // reads as active and would otherwise re-arm at a zero delay.
      const delay = Math.max(1, boundary.getTime() - adjustedNow.getTime());
      timeout = setTimeout(arm, Math.min(delay, MAX_TIMER));
    };

    arm();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [sale, serverTimeOffsetInMs]);

  return isSaleActive;
};

export const useSaleState = (sale?: Sale) => {
  const { isFlashDealUpcoming } = useFlashDeal(sale);
  const isSaleActive = useIsActiveSale(sale);

  return {
    active: isSaleActive,
    // Flash-deal-only by design (CPS-7269): the green "Sale starts" teaser is
    // reserved for flash deals, so an upcoming sale of any other type (incl.
    // a new-release discount) never reads as upcoming here.
    upcoming: isFlashDealUpcoming,
    start: sale?.dates.starts,
    end: sale?.dates.ends,
    type: sale?.type,
  };
};
